import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';

// Pretraga bez obzira na velika/mala slova i kvačice
const norm = (s) => String(s || '').toLowerCase().replace(/đ/g, 'dj').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const collator = new Intl.Collator('sr-Latn', { sensitivity: 'base', numeric: true });
const addressOf = (c) => [c.address, [c.postal_code, c.city].filter(Boolean).join(' '), c.is_foreign ? c.country : '']
  .filter(Boolean).join(', ');
const PAGE_SIZES = [5, 10, 25, 50];

// Inicijali za kružić: prve dve reči naziva, bez oznaka firme (DOO, PR, AD...)
const SKIP = new Set(['doo', 'd.o.o.', 'pr', 'ad', 'a.d.', 'szr', 'str', 'sur', 'ltd', 'gmbh', 'llc', 'inc']);
const initialsOf = (name) => {
  const words = String(name || '').split(/\s+/).filter((w) => w && !SKIP.has(w.toLowerCase()));
  const ini = words.slice(0, 2).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')[0] || '').join('');
  return (ini || String(name || '?')[0] || '?').toUpperCase();
};
// Kratak drugi red na telefonu: PIB · grad (strani: država · grad)
const subOf = (c) => {
  const place = c.is_foreign ? [c.city, c.country].filter(Boolean).join(', ') : c.city;
  return [c.pib ? `${c.is_foreign ? 'Tax ID' : 'PIB'} ${c.pib}` : '', place].filter(Boolean).join(' · ');
};

// Lista klijenata (tabela "customers") — kartice Svi / Domaći / Strani, pretraga, sortiranje, stranice
export default function ClientsList() {
  const { activeAgencyId } = useAgency();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all'); // all | dom | ino
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  // Brisanje sa dve provere
  const [toDelete, setToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [menuFor, setMenuFor] = useState(null); // telefon: otvoren meni ⋮ za ovog klijenta

  useEffect(() => {
    if (!menuFor) return undefined;
    const close = () => setMenuFor(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuFor]);

  useEffect(() => {
    if (!activeAgencyId) return;
    (async () => {
      setLoading(true);
      const { data, error: err } = await supabase.from('customers').select('*').eq('agency_id', activeAgencyId);
      if (err) setError(err.message);
      setCustomers(data || []);
      setLoading(false);
    })();
  }, [activeAgencyId]);

  const counts = useMemo(() => ({
    all: customers.length,
    dom: customers.filter((c) => !c.is_foreign).length,
    ino: customers.filter((c) => c.is_foreign).length,
  }), [customers]);

  const rows = useMemo(() => {
    const q = norm(query.trim());
    const list = customers
      .filter((c) => tab === 'all' || (tab === 'dom' ? !c.is_foreign : c.is_foreign))
      .filter((c) => !q || [c.name, addressOf(c), c.pib, c.mb].some((v) => norm(v).includes(q)));
    const val = (c) => (sort.key === 'address' ? addressOf(c) : c[sort.key]) || '';
    list.sort((a, b) => collator.compare(String(val(a)), String(val(b))) * (sort.dir === 'asc' ? 1 : -1));
    return list;
  }, [customers, tab, query, sort]);

  // Promena filtera vraća na prvu stranu
  useEffect(() => { setPage(0); }, [tab, query, pageSize]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const openDelete = (c) => { setMenuFor(null); setToDelete(c); setDeleteStep(1); setDeleteError(''); };
  const closeDelete = () => { if (!deleting) setToDelete(null); };

  const confirmDelete = async () => {
    setDeleteError('');
    setDeleting(true);
    const { count, error: countErr } = await supabase
      .from('invoices').select('id', { count: 'exact', head: true }).eq('customer_id', toDelete.id);
    if (countErr) { setDeleting(false); setDeleteError(countErr.message); return; }
    if (count > 0) { setDeleting(false); setDeleteError(t('customerHasInvoices', count)); return; }
    const { error: delErr } = await supabase.from('customers').delete().eq('id', toDelete.id);
    setDeleting(false);
    if (delErr) { setDeleteError(delErr.message); return; }
    setCustomers((prev) => prev.filter((c) => c.id !== toDelete.id));
    setToDelete(null);
  };

  const SortHead = ({ k, label }) => (
    <th style={styles.th}>
      <button type="button" onClick={() => toggleSort(k)} style={styles.sortBtn}>
        {label}
        <Icon
          name={sort.key === k ? (sort.dir === 'asc' ? 'chevronUp' : 'chevronDown') : 'chevronsUpDown'}
          size={14}
          strokeWidth={2.2}
          color={sort.key === k ? 'var(--lavender-700)' : 'var(--ink-300)'}
        />
      </button>
    </th>
  );

  const TABS = [
    { key: 'all', label: t('tabAllClients'), n: counts.all },
    { key: 'dom', label: t('tabDomestic'), n: counts.dom },
    { key: 'ino', label: t('tabForeign'), n: counts.ino },
  ];

  return (
    <div>
      {/* Naslov i dugme iznad kartice — isto kao na listi faktura */}
      <div style={styles.headerRow}>
        <h1 style={styles.heading}>{t('clients')}</h1>
        <button onClick={() => navigate('/klijenti/novi')} style={styles.addBtn}>
          <Icon name="userPlus" size={15} />
          {t('addClientBtn')}
        </button>
      </div>

      <div style={styles.card} className="cl-card">

        <div style={styles.toolbar}>
          <div style={styles.tabs} role="tablist" className="cl-tabs">
            {TABS.map((tb) => (
              <button
                key={tb.key}
                role="tab"
                aria-selected={tab === tb.key}
                onClick={() => setTab(tb.key)}
                className="cl-tab"
                style={{ ...styles.tab, ...(tab === tb.key ? styles.tabActive : {}) }}
              >
                {tb.label}
                <span style={{ ...styles.tabCount, ...(tab === tb.key ? styles.tabCountActive : {}) }}>{tb.n}</span>
              </button>
            ))}
          </div>
          <div style={styles.toolsRight} className="cl-tools">
            <label style={styles.searchBox} className="cl-search">
              <Icon name="search" size={15} color="var(--ink-500)" strokeWidth={1.9} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('searchClients')}
                aria-label={t('searchClients')}
                style={styles.searchInput}
              />
            </label>
            <label style={styles.sizeBox} className="cl-size">
              <span>{t('showLabel')}</span>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={styles.sizeSelect}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.empty}>{t('loadingGeneric')}</div>
        ) : rows.length === 0 ? (
          <div style={styles.empty}>{customers.length === 0 ? t('noClientsYet') : t('noSearchResults')}</div>
        ) : (
          <>
            {/* Telefon: pregledna lista umesto široke tabele */}
            <div style={styles.mList} className="cl-mobile">
              {pageRows.map((c) => (
                <div key={c.id} style={styles.mRow}>
                  <button onClick={() => navigate(`/klijenti/${c.id}`)} style={styles.mMain} aria-label={`${t('viewBtn')}: ${c.name}`}>
                    <span style={{ ...styles.mAvatar, ...(c.is_foreign ? styles.mAvatarForeign : {}) }}>{initialsOf(c.name)}</span>
                    <span style={styles.mText}>
                      <span style={styles.mName}>{c.name}</span>
                      <span style={styles.mSub}>
                        {c.is_foreign && <span style={styles.mTag}>{t('tabForeign')}</span>}
                        {subOf(c) || '—'}
                      </span>
                    </span>
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === c.id ? null : c.id); }}
                      style={styles.mMore}
                      aria-label={`${t('editBtn')} / ${t('deleteBtn')}: ${c.name}`}
                      aria-expanded={menuFor === c.id}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
                    </button>
                    {menuFor === c.id && (
                      <div style={styles.mMenu} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => navigate(`/klijenti/${c.id}`)} style={styles.mMenuItem}><Icon name="eye" size={16} />{t('viewBtn')}</button>
                        <button onClick={() => navigate(`/klijenti/${c.id}/izmeni`)} style={styles.mMenuItem}><Icon name="pencil" size={16} />{t('editBtn')}</button>
                        <button onClick={() => openDelete(c)} style={{ ...styles.mMenuItem, color: 'var(--red-600)' }}><Icon name="trash" size={16} />{t('deleteBtn')}</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={styles.tableWrap} className="cl-desktop">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <SortHead k="name" label={t('colName')} />
                    <SortHead k="address" label={t('colAddress')} />
                    <SortHead k="pib" label="PIB" />
                    <SortHead k="mb" label={t('colMb')} />
                    <th style={{ ...styles.th, width: 150 }} aria-label="Akcije" />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((c) => (
                    <tr key={c.id} style={styles.tr}>
                      <td style={styles.td}>
                        <button onClick={() => navigate(`/klijenti/${c.id}`)} style={styles.nameLink}>{c.name}</button>
                        {c.is_foreign && <span style={styles.foreignTag}>{t('tabForeign')}</span>}
                      </td>
                      <td style={{ ...styles.td, color: 'var(--ink-700)' }}>{addressOf(c) || '—'}</td>
                      <td style={styles.tdMono}>{c.pib || '—'}</td>
                      <td style={styles.tdMono}>{c.mb || '—'}</td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <button onClick={() => navigate(`/klijenti/${c.id}`)} style={styles.actBtn} title={t('viewBtn')} aria-label={`${t('viewBtn')}: ${c.name}`}>
                            <Icon name="eye" size={14} />
                          </button>
                          <button onClick={() => navigate(`/klijenti/${c.id}/izmeni`)} style={styles.actBtn} title={t('editBtn')} aria-label={`${t('editBtn')}: ${c.name}`}>
                            <Icon name="pencil" size={14} />
                          </button>
                          <button onClick={() => openDelete(c)} style={{ ...styles.actBtn, ...styles.actBtnDanger }} title={t('deleteBtn')} aria-label={`${t('deleteBtn')}: ${c.name}`}>
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.pager} className="cl-pager">
              <span style={styles.pagerText}>
                {t('pageRange', safePage * pageSize + 1, Math.min((safePage + 1) * pageSize, rows.length), rows.length)}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setPage(safePage - 1)} disabled={safePage === 0} style={styles.pageBtn}>
                  <Icon name="arrowLeft" size={15} />
                  {t('prevPage')}
                </button>
                <button onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount - 1} style={styles.pageBtn}>
                  {t('nextPage')}
                  <Icon name="chevronRight" size={15} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {toDelete && (
        <div style={styles.overlay} onClick={closeDelete}>
          <div style={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalIcon}><Icon name="trash" size={20} color="var(--red-600)" /></div>
            <div style={{ ...styles.modalText, ...(deleteStep === 2 ? { color: 'var(--red-600)', fontWeight: 700 } : {}) }}>
              {deleteStep === 1 ? t('deleteCustomerConfirm1', toDelete.name) : t('deleteCustomerConfirm2')}
            </div>
            {deleteError && <div style={styles.errorBox}>{deleteError}</div>}
            <div style={styles.modalRow}>
              <button onClick={closeDelete} disabled={deleting} style={styles.cancelBtn}>{t('cancelBtn')}</button>
              {deleteStep === 1 ? (
                <button onClick={() => setDeleteStep(2)} style={styles.continueBtn}>{t('deleteContinueBtn')}</button>
              ) : (
                <button onClick={confirmDelete} disabled={deleting} style={styles.dangerBtn}>
                  {deleting ? t('deletingEllipsis') : t('deleteFinalBtn')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cl-mobile { display: none; }
        @media (max-width: 760px) {
          .cl-desktop { display: none !important; }
          .cl-mobile { display: block !important; }
          .cl-card { padding: 16px !important; border-radius: 16px !important; }
          .cl-tabs { width: 100%; flex-wrap: nowrap !important; }
          .cl-tab { flex: 1 1 0; justify-content: center; padding: 0 8px !important; }
          .cl-tools { width: 100%; }
          .cl-search { flex: 1 1 auto; min-width: 0 !important; min-height: 44px !important; }
          .cl-search input { font-size: 16px !important; }
          .cl-size { min-height: 44px !important; }
          .cl-pager { justify-content: center !important; text-align: center; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 14px 6px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  heading: { fontSize: 'var(--fs-page, 22px)', margin: 0 },
  addBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13 },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tab: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 32, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', borderRadius: 8, padding: '0 11px', fontWeight: 700, fontSize: 13 },
  tabActive: { background: 'var(--teal-800)', borderColor: 'var(--teal-800)', color: '#fff' },
  tabCount: { fontSize: 11, fontWeight: 600, background: 'var(--paper)', color: 'var(--ink-700)', borderRadius: 6, padding: '1px 6px', minWidth: 18, textAlign: 'center' },
  tabCountActive: { background: 'rgba(255,255,255,0.18)', color: '#fff' },
  toolsRight: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  searchBox: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: '0 12px', minWidth: 220 },
  searchInput: { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 14 },
  sizeBox: { display: 'flex', alignItems: 'center', gap: 4, minHeight: 36, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: '0 4px 0 10px', fontSize: 12, color: 'var(--ink-500)' },
  sizeSelect: { border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: 'var(--teal-800)' },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12, wordBreak: 'break-word' },
  empty: { textAlign: 'center', color: 'var(--ink-500)', fontSize: 13, padding: '40px 0' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 720 },
  th: { textAlign: 'left', background: 'var(--paper)', padding: '0 14px', height: 34, borderBottom: '1px solid var(--line)' },
  sortBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', padding: 0, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' },
  tr: {},
  td: { padding: '10px 14px', borderBottom: '1px solid var(--line)', fontSize: 13, color: 'var(--ink-900)', verticalAlign: 'middle' },
  tdMono: { padding: '10px 14px', borderBottom: '1px solid var(--line)', fontSize: 13, color: 'var(--ink-700)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  nameLink: { border: 'none', background: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-900)', textAlign: 'left', cursor: 'pointer' },
  foreignTag: { marginLeft: 8, fontSize: 10.5, fontWeight: 700, background: 'var(--lavender-100)', color: 'var(--lavender-700)', borderRadius: 5, padding: '1px 6px' },
  actions: { display: 'flex', gap: 4, justifyContent: 'flex-end' },
  actBtn: { width: 30, height: 30, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  actBtnDanger: { color: 'var(--red-600)' },
  mList: { border: '1px solid var(--line)', borderRadius: 10, overflow: 'visible' },
  mRow: { display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--line)', paddingRight: 4 },
  mMain: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px 12px 14px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
  mAvatar: { width: 40, height: 40, borderRadius: 8, background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 },
  mAvatarForeign: { background: 'var(--lavender-100)', color: 'var(--lavender-700)' },
  mText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  mName: { fontSize: 15, fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mSub: { fontSize: 12.5, color: 'var(--ink-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 },
  mTag: { fontSize: 10.5, fontWeight: 700, background: 'var(--lavender-100)', color: 'var(--lavender-700)', borderRadius: 5, padding: '1px 6px', flexShrink: 0 },
  mMore: { width: 44, height: 44, border: 'none', background: 'transparent', borderRadius: 10, color: 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  mMenu: { position: 'absolute', right: 0, top: 46, zIndex: 10, width: 180, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: 6 },
  mMenuItem: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, border: 'none', background: 'transparent', borderRadius: 8, padding: '0 10px', fontSize: 14, fontWeight: 600, color: 'var(--ink-700)', textAlign: 'left', cursor: 'pointer' },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 4px' },
  pagerText: { fontSize: 12, color: 'var(--ink-500)' },
  pageBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 32, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, padding: '0 10px', fontWeight: 600, fontSize: 12.5, color: 'var(--ink-700)' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,37,48,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: 'var(--surface)', borderRadius: 10, padding: 24, width: '100%', maxWidth: 420, boxShadow: 'var(--shadow-lg)' },
  modalIcon: { width: 44, height: 44, borderRadius: 8, background: 'var(--red-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 },
  modalText: { fontSize: 15, lineHeight: 1.5, color: 'var(--ink-900)', marginBottom: 16 },
  modalRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' },
  cancelBtn: { minHeight: 42, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '0 14px', fontWeight: 600, fontSize: 14, color: 'var(--ink-700)' },
  continueBtn: { minHeight: 42, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 14 },
  dangerBtn: { minHeight: 42, border: 'none', background: 'var(--red-600)', color: '#fff', borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 14 },
};
